import apiClient from '@/api/client'

export interface ThreadSummary {
  id: string
  name: string
  last_message_preview?: string
  last_message_at?: string // ISO
  unread_count?: number
}

export interface MessageReactionSummary { emoji: string; count: number; mine: boolean }

export interface MessageItem {
  id: string
  thread_id: string
  sender_id?: string
  sender_name: string
  sender_role?: string
  body: string
  created_at: string // ISO
  edited_at?: string | null
  attachment_ids?: string[]
  reactions?: MessageReactionSummary[]
}

/** Spec section 7 (Message Center): the 17 available reactions. */
export const REACTION_EMOJIS = ['❤️', '💯', '✅', '🙏', '😂', '😢', '👍', '👎', '🔥', '👀', '✍️', '👏', '😱', '🏆', '🫡', '⚡', '😕']

export interface ParishUserResult {
  id: string
  name: string
  role: string
  email?: string
  parish_id?: string
  parish_name?: string
}

export interface CreateThreadPayload {
  name?: string
  participant_ids: string[]
  initial_body?: string
}

export interface SendMessagePayload {
  body: string
  attachment_ids?: string[]
}

export const messagingApi = {
  async listThreads(): Promise<ThreadSummary[]> {
    const { data } = await apiClient.get('/api/v1/messaging/threads')
    return data
  },
  async getMessages(threadId: string, params?: { before?: string; limit?: number }): Promise<MessageItem[]> {
    const { data } = await apiClient.get(`/api/v1/messaging/threads/${threadId}/messages`, { params })
    return data
  },
  async createThread(payload: CreateThreadPayload): Promise<{ thread: ThreadSummary; firstMessage?: MessageItem }> {
    const { data } = await apiClient.post('/api/v1/messaging/threads', payload)
    return data
  },
  async sendMessage(threadId: string, payload: SendMessagePayload): Promise<MessageItem> {
    const { data } = await apiClient.post(`/api/v1/messaging/threads/${threadId}/messages`, payload)
    return data
  },
  async searchUsers(query: string): Promise<ParishUserResult[]> {
    const { data } = await apiClient.get(`/api/v1/messaging/search-users`, { params: { q: query } })
    return data
  },
  async updateThread(threadId: string, payload: { name?: string; participant_ids?: string[] }): Promise<ThreadSummary> {
    const { data } = await apiClient.put(`/api/v1/messaging/threads/${threadId}`, payload)
    return data
  },
  async deleteMessage(messageId: string): Promise<void> {
    await apiClient.delete(`/api/v1/messaging/${messageId}`)
  },
  async updateMessage(messageId: string, payload: { body: string }): Promise<MessageItem> {
    const { data } = await apiClient.put(`/api/v1/messaging/${messageId}`, payload)
    return data
  },
  async hideThread(threadId: string): Promise<void> {
    await apiClient.post(`/api/v1/messaging/threads/${threadId}/hide`)
  },
  async restoreThread(threadId: string): Promise<void> {
    await apiClient.post(`/api/v1/messaging/threads/${threadId}/restore`)
  },
  async getMessageSeenBy(messageId: string): Promise<Array<{ id: string; name: string }>> {
    const { data } = await apiClient.get(`/api/v1/messaging/${messageId}/seen-by`)
    return Array.isArray(data) ? data : []
  },
  async getMessageReactedBy(messageId: string): Promise<Array<{ id: string; name: string; emoji: string }>> {
    const { data } = await apiClient.get(`/api/v1/messaging/${messageId}/reactions`)
    return Array.isArray(data) ? data : []
  },
  async reactToMessage(messageId: string, emoji: string): Promise<{ active: boolean; reactions: MessageReactionSummary[] }> {
    const { data } = await apiClient.post(`/api/v1/messaging/${messageId}/react`, { emoji })
    return data
  },
}


